import { ChartData } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { ChartBase } from 'components/common';
import { CardBackground } from 'components/ui';
import { isEmpty, orderBy } from 'lodash';
import {
  useGetEmailCampaignRevenueAttributionQuery,
  useGetStoreInfoQuery,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';

export default function CampaignRevenueAttribution() {
  const { rawQueryParams, serializedStoreName } = useParseURLQueryParams();

  const {
    data,
    isFetching: loading,
  } = useGetEmailCampaignRevenueAttributionQuery(
    { serializedStoreName: serializedStoreName },
    { skip: !!!rawQueryParams?.store_name }
  );

  const { data: storeInfo } = useGetStoreInfoQuery(serializedStoreName, {
    skip: !!!rawQueryParams?.store_name,
  });

  const currency = storeInfo?.currency ?? 'usd';
  const locale = storeInfo?.primary_locale ?? 'en';

  let campaignRevenueTotal = data?.chart_data.reduce(function (a, b) {
    return a + b.revenue;
  }, 0);

  const sortedData = orderBy(data?.chart_data, ['revenue'], ['desc']);

  const labels = sortedData?.map((item) => [
    item.segment_name,
    item.service.replaceAll(',', ', '),
  ]);

  const chartData: ChartData = {
    labels: labels,
    datasets: [
      {
        data: sortedData?.map((item) => item.revenue),
        fill: false,
        backgroundColor: ['#FC9000'],
        borderColor: ['#FC9000'],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 16 },
          callback: function (value: number) {
            return value.toLocaleString(locale, {
              style: 'currency',
              currency: currency,
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            });
          },
        },
      },
      y: {
        grid: { display: false },
        // This is just space holder for the custom plugin
        ticks: { font: { size: 20, weight: 'bold' }, color: 'transparent' },
      },
    },

    responsive: true,
    plugins: {
      datalabels: {
        align: 'end',
        anchor: 'end',
        offset: 10,
        font: {
          size: 16,
          weight: 'bold',
        },
        formatter: function (value: number, context: Context) {
          return value.toLocaleString(locale, {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          });
        },
      },

      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },

    events: [],
  };

  const customSubLabelsPlugin = {
    id: 'customSubLabels',
    // @ts-ignore
    afterDraw: (chart, args, opts) => {
      const {
        ctx,
        scales: { y },
      } = chart;
      const labelItems = y._labelItems;
      const fontStringMain = 'bold 20px Arimo';
      const fontStringSubTitle = '13px Arimo';

      // loop over each dataset label
      for (let i = 0; i < labelItems.length; i++) {
        let labelItem = labelItems[i];

        // For purposes of redrawing, we are going to always assume that each label is an array - because we make it that way if we need to
        const label = Array.isArray(labelItem.label)
          ? labelItem.label
          : [labelItem.label];

        // Draw new text on canvas
        let offset = 0;
        label.forEach((el: any) => {
          let elTextMetrics = ctx.measureText(el);
          let lineheight;
          if (labelItem.label.indexOf(el) === 0) {
            ctx.font = fontStringMain;
            lineheight = 0;
          } else {
            ctx.font = fontStringSubTitle;
            lineheight = 3;
          }

          ctx.save();
          // Here you can change the text color
          ctx.fillStyle = '#546a6f';
          ctx.fillText(
            el,
            // This Defines the position of the text - first is horizontal, second is vertical
            0,
            labelItem.translation[1] + offset + lineheight
          );
          ctx.restore();
          offset +=
            elTextMetrics.fontBoundingBoxAscent +
            elTextMetrics.fontBoundingBoxDescent;
        });
      }
    },
  };

  return (
    <div className="flex flex-col mt-10">
      <h4 className="text-base tracking-wide md:text-2xl">
        Segment campaign ROI
      </h4>
      <p className="text-black text-segments-13px md:text-base text-opacity-30">
        New sales attributable to campaigns based on your synced segments, from
        the last 14 days.
      </p>

      <div className="mt-2 md:grid md:mx-0 md:mt-6 md:gap-x-6">
        <div className="mt-6 md:mt-0">
          <CardBackground>
            {loading ? (
              <div className="flex items-center justify-center p-10">
                Loading...
              </div>
            ) : isEmpty(data?.chart_data) ? (
              <div className="flex items-center justify-center p-10">
                {data?.message ||
                  'Loading this module has failed. Please reach out to our support team if this continues.'}
              </div>
            ) : (
              <div className="px-5 py-4 lg:px-10 lg:py-6">
                <p className="text-2xl">
                  Total sales:{' '}
                  {campaignRevenueTotal?.toLocaleString(locale, {
                    style: 'currency',
                    currency: currency,
                  })}
                </p>
                <ChartBase
                  type="bar"
                  data={chartData}
                  options={options}
                  plugins={[ChartDataLabels, customSubLabelsPlugin]}
                />
                <div className="mt-4 text-xs text-gray-400">
                  Estimated values based on most recent data available.
                </div>
              </div>
            )}
          </CardBackground>
        </div>
      </div>
    </div>
  );
}
